import React, { useState, useEffect } from "react";
import { Form, Button, Card, Row, Col, Alert } from "react-bootstrap";
import useLoadService from "../../../../services/loadService";
import { useToast } from "../../../../context/toatsContext";
import { API_URL } from "../../../../helpers";

// ---------- helpers: сжатие изображений без библиотек ----------
const blobToFile = (blob, name) => {
  try { return new File([blob], name, { type: blob.type }); }
  catch {
    // iOS 13/старые браузеры
    blob.name = name;
    blob.lastModified = Date.now();
    return blob;
  }
};

const drawToCanvas = async (bitmap, maxW, maxH) => {
  const ratio = Math.min(maxW / bitmap.width, maxH / bitmap.height, 1);
  const w = Math.round(bitmap.width * ratio);
  const h = Math.round(bitmap.height * ratio);
  const canvas = typeof OffscreenCanvas !== "undefined"
    ? new OffscreenCanvas(w, h)
    : Object.assign(document.createElement("canvas"), { width: w, height: h });
  if (!(canvas instanceof OffscreenCanvas)) { canvas.width = w; canvas.height = h; }
  const ctx = canvas.getContext("2d");
  ctx.drawImage(bitmap, 0, 0, w, h);
  return canvas;
};

const canvasToBlob = (canvas, type, quality) => new Promise((res, rej) => {
  if (canvas.convertToBlob) {
    canvas.convertToBlob({ type, quality }).then(res).catch(rej);
  } else {
    canvas.toBlob(b => b ? res(b) : rej(new Error("toBlob failed")), type, quality);
  }
});

const optimizeImageFile = async (file, { maxWidth = 1600, maxHeight = 1600, quality = 0.82, format = "image/webp" } = {}) => {
  const bitmap = await createImageBitmap(file);
  const canvas = await drawToCanvas(bitmap, maxWidth, maxHeight);
  const outBlob = await canvasToBlob(canvas, format, quality);
  const newExt = format.includes("webp") ? "webp" : format.includes("jpeg") ? "jpg" : "png";
  const base = file.name.replace(/\.[a-z0-9]+$/i, "");
  return blobToFile(outBlob, `${base}.${newExt}`);
};

const optimizeMany = async (files, opts) => {
  const results = [];
  for (const f of files) {
    if (!/^image\//i.test(f.type)) { results.push(f); continue; }        // не-изображения (PDF) не трогаем
    if (f.size < 180 * 1024) { results.push(f); continue; }             // мелкие файлы пропускаем
    results.push(await optimizeImageFile(f, opts));
  }
  return results;
};

// ---------- компонент ----------
const LoadForm = ({ initialData, onSubmit, onCancel }) => {
  const { handleToast } = useToast();
  const { createLoad, updateLoad } = useLoadService();

  // форма
  const [formData, setFormData] = useState({
    type: "Cars",
    Vin: "",
    category: "",
    companyName: "",
    buyer: { name: "", contact: "", email: "" },
    carrier: { name: "", contact: "", email: "" },
    pickUpLocation: { city: "", state: "", loc: "", contactPhone: "" },
    deliveryLocation: { city: "", state: "", loc: "", contactPhone: "" },
    deliveryDate: "",
    pickUpDate: "",
    status: "in process",
    costomerEmail: [],
    vehicleDetails: { make: "", model: "", year: "", color: "", mileage: "" },
    specialRequirements: "",
    insurance: false,
    value: "",
    aging: 0,
    tracing: "",
    assignedDate: new Date().toISOString().split("T")[0],
  });

  // vehicle images
  const [existingVehicleImages, setExistingVehicleImages] = useState([]);
  const [newVehicleImages, setNewVehicleImages] = useState([]);
  const [removedVehicleImages, setRemovedVehicleImages] = useState([]);

  // BOL (images/pdf)
  const [existingBOL, setExistingBOL] = useState([]);
  const [newBOL, setNewBOL] = useState([]);
  const [removedBOL, setRemovedBOL] = useState([]);

  // carrier images
  const [existingCarrierImages, setExistingCarrierImages] = useState([]);
  const [newCarrierImages, setNewCarrierImages] = useState([]);
  const [removedCarrierImages, setRemovedCarrierImages] = useState([]);

  // статусные флаги/ошибки
  const [errors, setErrors] = useState({});
  const [compressingVehicle, setCompressingVehicle] = useState(false);
  const [compressingCarrier, setCompressingCarrier] = useState(false);
  const [compressingBOL, setCompressingBOL] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const transportTypes = ["Boats", "Cars", "Motorcycles", "RVs"];
  const statusOptions = ["in process", "picked up", "delivered", "cancelled"];

  useEffect(() => {
    if (initialData) {
      const formattedData = {
        ...initialData,
        deliveryDate: initialData.deliveryDate?.split("T")[0] || "",
        pickUpDate: initialData.pickUpDate?.split("T")[0] || "",
        assignedDate:
          initialData.assignedDate?.split("T")[0] ||
          new Date().toISOString().split("T")[0],
        vehicleDetails: initialData.vehicleDetails || {
          make: "",
          model: "",
          year: "",
          color: "",
          mileage: "",
        },
      };
      setFormData(formattedData);
      setExistingVehicleImages(initialData.images || []);
      setExistingCarrierImages(initialData.carrier?.carrierImageFile || []);
      setExistingBOL(initialData.BOL || []);
    }
  }, [initialData]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name.includes(".")) {
      const [parent, child] = name.split(".");
      setFormData(prev => ({ ...prev, [parent]: { ...prev[parent], [child]: type === "checkbox" ? checked : value } }));
    } else {
      setFormData(prev => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
    }
  };

  // ------ carrier images ------
  const handleCarrierImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const valid = files.filter(f => /\.(jpg|jpeg|png|webp)$/i.test(f.name));
    if (valid.length !== files.length) {
      setErrors(prev => ({ ...prev, carrierImages: "Only JPG, JPEG, PNG, WEBP formats allowed" }));
      return;
    }
    setCompressingCarrier(true);
    try {
      const optimized = await optimizeMany(valid, { maxWidth: 1600, maxHeight: 1600, quality: 0.82, format: "image/webp" });
      setNewCarrierImages(prev => [...prev, ...optimized]);
      setErrors(prev => ({ ...prev, carrierImages: null }));
    } catch {
      setErrors(prev => ({ ...prev, carrierImages: "Image optimization failed" }));
    } finally {
      setCompressingCarrier(false);
    }
  };

  const removeCarrierImage = (index, type) => {
    if (type === "existing") {
      const removed = existingCarrierImages[index];
      if (existingCarrierImages.length === 1) {
        setRemovedCarrierImages(prev => [...prev, ...existingCarrierImages]);
        setExistingCarrierImages([]);
      } else {
        setRemovedCarrierImages(prev => [...prev, removed]);
        setExistingCarrierImages(prev => prev.filter((_, i) => i !== index));
      }
    } else {
      setNewCarrierImages(prev => prev.filter((_, i) => i !== index));
    }
  };

  // ------ vehicle images ------
  const handleVehicleImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const valid = files.filter(f => /\.(jpg|jpeg|png|webp)$/i.test(f.name));
    if (valid.length !== files.length) {
      setErrors(prev => ({ ...prev, images: "Only JPG, JPEG, PNG, WEBP formats allowed" }));
      return;
    }
    setCompressingVehicle(true);
    try {
      const optimized = await optimizeMany(valid, { maxWidth: 1600, maxHeight: 1600, quality: 0.82, format: "image/webp" });
      setNewVehicleImages(prev => [...prev, ...optimized]);
      setErrors(prev => ({ ...prev, images: null }));
    } catch {
      setErrors(prev => ({ ...prev, images: "Image optimization failed" }));
    } finally {
      setCompressingVehicle(false);
    }
  };

  const removeVehicleImage = (index, type) => {
    if (type === "existing") {
      const removed = existingVehicleImages[index];
      if (existingVehicleImages.length === 1) {
        setRemovedVehicleImages(prev => [...prev, ...existingVehicleImages]);
        setExistingVehicleImages([]);
      } else {
        setRemovedVehicleImages(prev => [...prev, removed]);
        setExistingVehicleImages(prev => prev.filter((_, i) => i !== index));
      }
    } else {
      setNewVehicleImages(prev => prev.filter((_, i) => i !== index));
    }
  };

  // ------ BOL (images + pdf) ------
  const handleBoLUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const valid = files.filter(f => /\.(jpg|jpeg|png|webp|pdf)$/i.test(f.name));
    if (valid.length !== files.length) {
      setErrors(prev => ({ ...prev, BOL: "Only JPG, JPEG, PNG, WEBP, or PDF allowed" }));
      return;
    }
    setCompressingBOL(true);
    try {
      const imgs = valid.filter(f => /^image\//i.test(f.type));
      const pdfs = valid.filter(f => f.type === "application/pdf" || /\.pdf$/i.test(f.name));
      const optimizedImgs = await optimizeMany(imgs, { maxWidth: 1600, maxHeight: 1600, quality: 0.82, format: "image/webp" });
      setNewBOL(prev => [...prev, ...optimizedImgs, ...pdfs]);
      setErrors(prev => ({ ...prev, BOL: null }));
    } catch {
      setErrors(prev => ({ ...prev, BOL: "BOL optimization failed" }));
    } finally {
      setCompressingBOL(false);
    }
  };

  const removeBoL = (index, type) => {
    if (type === "existing") {
      const removed = existingBOL[index];
      if (existingBOL.length === 1) {
        setRemovedBOL(prev => [...prev, ...existingBOL]);
        setExistingBOL([]);
      } else {
        setRemovedBOL(prev => [...prev, removed]);
        setExistingBOL(prev => prev.filter((_, i) => i !== index));
      }
    } else {
      setNewBOL(prev => prev.filter((_, i) => i !== index));
    }
  };

  // валидация
  const validateForm = () => {
    const nerr = {};
    const required = [
      "Vin",
      "type",
      "carrier.name",
      "pickUpLocation.city",
      "pickUpLocation.state",
      "deliveryLocation.city",
      "deliveryLocation.state",
    ];
    required.forEach(path => {
      const parts = path.split(".");
      let v = formData;
      parts.forEach(p => v = v?.[p]);
      if (!v) nerr[path] = `${path.replace(".", " ")} is required`;
    });
    setErrors(nerr);
    return Object.keys(nerr).length === 0;
  };

  // отправка
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setSubmitting(true);
      if (initialData) {
        await updateLoad(
          formData,
          newVehicleImages,
          newCarrierImages,
          removedVehicleImages,
          removedCarrierImages,
          newBOL,
          removedBOL
        );
      } else {
        await createLoad(
          formData,
          newVehicleImages,
          newCarrierImages,
          newBOL
        );
      }
      handleToast("success", initialData ? "Updated" : "Created");
    } catch (err) {
      handleToast("error", err?.message || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  // превью
  const newVehicleImageUrls = newVehicleImages.map(f => URL.createObjectURL(f));
  const newCarrierImageUrls = newCarrierImages.map(f => URL.createObjectURL(f));

  const existingBOLItems = (existingBOL || []).map((item) => ({
    src: typeof item === "string" ? `${API_URL}/${item}` : "",
    isPdf: typeof item === "string" ? /\.pdf$/i.test(item) : false,
    name: typeof item === "string" ? item.split("/").pop() : "BOL",
  }));
  const newBOLItems = (newBOL || []).map((file) => ({
    src: URL.createObjectURL(file),
    isPdf: file.type === "application/pdf" || /\.pdf$/i.test(file.name),
    name: file.name,
  }));

  // emails
  const addCustomerEmail = () => {
    setFormData(prev => ({ ...prev, costomerEmail: [...prev.costomerEmail, ""] }));
  };
  const removeCustomerEmail = (index) => {
    setFormData(prev => ({ ...prev, costomerEmail: prev.costomerEmail.filter((_, i) => i !== index) }));
  };
  const handleCustomerEmailChange = (index, value) => {
    setFormData(prev => {
      const arr = [...prev.costomerEmail];
      arr[index] = value;
      return { ...prev, costomerEmail: arr };
    });
  };

  return (
    <Card className="p-3 mb-4">
      <h4 className="mb-4">{initialData ? "Edit Load" : "Add New Load"}</h4>

      {Object.keys(errors).length > 0 && (
        <Alert variant="danger">
          {Object.values(errors).map((err, i) => <div key={i}>{err}</div>)}
        </Alert>
      )}

      <Form onSubmit={handleSubmit}>
        <Row className="mb-3">
          <Col md={3}>
            <Form.Group>
              <Form.Label>VIN *</Form.Label>
              <Form.Control
                type="text"
                name="Vin"
                value={formData.Vin}
                onChange={handleChange}
                isInvalid={!!errors.Vin}
              />
              <Form.Control.Feedback type="invalid">{errors.Vin}</Form.Control.Feedback>
            </Form.Group>

            <Form.Group>
              <Form.Label>Customer Emails</Form.Label>
              {formData.costomerEmail.map((email, index) => (
                <div key={index} className="d-flex align-items-center mb-2">
                  <Form.Control
                    type="email"
                    value={email}
                    onChange={(e) => handleCustomerEmailChange(index, e.target.value)}
                    placeholder="customer@example.com"
                    className="me-2"
                  />
                  <Button variant="danger" onClick={() => removeCustomerEmail(index)}>&times;</Button>
                </div>
              ))}
              <Button variant="outline-primary" size="sm" onClick={addCustomerEmail} className="mt-2">
                Add Email
              </Button>
            </Form.Group>
          </Col>

          <Col md={2}>
            <Form.Group className="mb-2">
              <Form.Label>Company Name</Form.Label>
              <Form.Control
                type="text"
                name="companyName"
                value={formData.companyName || ""}
                onChange={handleChange}
                isInvalid={!!errors.companyName}
              />
            </Form.Group>
          </Col>

          <Col md={2}>
            <Form.Group>
              <Form.Label>Type *</Form.Label>
              <Form.Select
                name="type"
                value={formData.type}
                onChange={handleChange}
                isInvalid={!!errors.type}
              >
                {transportTypes.map((t) => <option key={t} value={t}>{t}</option>)}
              </Form.Select>
            </Form.Group>
          </Col>

          <Col md={2}>
            <Form.Group>
              <Form.Label>Status</Form.Label>
              <Form.Select name="status" value={formData.status} onChange={handleChange}>
                {statusOptions.map((s) => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
              </Form.Select>
            </Form.Group>
          </Col>

          <Col md={2}>
            <Form.Group>
              <Form.Label>Insurance</Form.Label>
              <div className="mt-2">
                <Form.Check
                  type="switch"
                  name="insurance"
                  label={formData.insurance ? "Insured" : "Not Insured"}
                  checked={formData.insurance}
                  onChange={handleChange}
                />
              </div>
            </Form.Group>
          </Col>
        </Row>

        {/* Driver Information */}
        <Row className="mb-3">
          <Col>
            <Card className="p-3 mb-3">
              <h5>Driver Information</h5>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-2">
                    <Form.Label>Name *</Form.Label>
                    <Form.Control
                      type="text"
                      name="carrier.name"
                      value={formData.carrier?.name || ""}
                      onChange={handleChange}
                      isInvalid={!!errors["carrier.name"]}
                    />
                    <Form.Control.Feedback type="invalid">{errors["carrier.name"]}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-2">
                    <Form.Label>Phone *</Form.Label>
                    <Form.Control
                      type="text"
                      name="carrier.contact"
                      value={formData.carrier?.contact || ""}
                      onChange={handleChange}
                      isInvalid={!!errors["carrier.contact"]}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Form.Group>
                <Form.Label>Email</Form.Label>
                <Form.Control type="email" name="carrier.email" value={formData.carrier?.email || ""} onChange={handleChange} />
              </Form.Group>

              <Form.Group className="mt-3">
                <Form.Label>Driver Photos</Form.Label>
                <Form.Control type="file" multiple accept="image/*" onChange={handleCarrierImageUpload} />
                {compressingCarrier && <div className="mt-2 small text-muted">Optimizing images…</div>}
                {errors.carrierImages && <Form.Text className="text-danger">{errors.carrierImages}</Form.Text>}
              </Form.Group>

              <div className="d-flex flex-wrap mt-3">
                {existingCarrierImages.map((url, index) => (
                  <div key={`existing-${index}`} className="position-relative me-2 mb-2">
                    <img
                      src={`${API_URL}/${url}`}
                      alt={`Driver ${index}`}
                      className="img-thumbnail"
                      style={{ width: 100, height: 100, objectFit: "cover" }}
                    />
                    <Button
                      variant="danger"
                      size="sm"
                      className="position-absolute top-0 end-0"
                      onClick={() => removeCarrierImage(index, "existing")}
                      style={{ transform: "translate(50%, -50%)" }}
                    >
                      &times;
                    </Button>
                  </div>
                ))}
                {newCarrierImageUrls.map((url, index) => (
                  <div key={`new-${index}`} className="position-relative me-2 mb-2">
                    <img
                      src={url}
                      alt={`New driver ${index}`}
                      className="img-thumbnail"
                      style={{ width: 100, height: 100, objectFit: "cover" }}
                    />
                    <Button
                      variant="danger"
                      size="sm"
                      className="position-absolute top-0 end-0"
                      onClick={() => removeCarrierImage(index, "new")}
                      style={{ transform: "translate(50%, -50%)" }}
                    >
                      &times;
                    </Button>
                  </div>
                ))}
              </div>
            </Card>
          </Col>
        </Row>

        {/* BOL */}
        <Row className="mb-3">
          <Col>
            <Card className="p-3 mb-3">
              <h5>Bill of Lading (BOL)</h5>
              <Form.Group className="mt-2">
                <Form.Label>Upload BOL (images or PDF)</Form.Label>
                <Form.Control type="file" multiple accept="image/*,application/pdf,.pdf" onChange={handleBoLUpload} />
                {compressingBOL && <div className="mt-2 small text-muted">Optimizing BOL files…</div>}
                {errors.BOL && <Form.Text className="text-danger">{errors.BOL}</Form.Text>}
              </Form.Group>

              <div className="d-flex flex-wrap mt-3">
                {existingBOLItems.map((item, index) => (
                  <div key={`bol-existing-${index}`} className="position-relative me-2 mb-2">
                    {item.isPdf ? (
                      <a href={item.src} target="_blank" rel="noreferrer" className="btn btn-outline-secondary btn-sm" title={item.name}>
                        {item.name || "BOL.pdf"}
                      </a>
                    ) : (
                      <img
                        src={item.src}
                        alt={`BOL ${index}`}
                        className="img-thumbnail"
                        style={{ width: 100, height: 100, objectFit: "cover" }}
                      />
                    )}
                    <Button
                      variant="danger"
                      size="sm"
                      className="position-absolute top-0 end-0"
                      onClick={() => removeBoL(index, "existing")}
                      style={{ transform: "translate(50%, -50%)" }}
                    >
                      &times;
                    </Button>
                  </div>
                ))}
                {newBOLItems.map((item, index) => (
                  <div key={`bol-new-${index}`} className="position-relative me-2 mb-2">
                    {item.isPdf ? (
                      <span className="btn btn-outline-secondary btn-sm" title={item.name}>
                        {item.name}
                      </span>
                    ) : (
                      <img
                        src={item.src}
                        alt={`New BOL ${index}`}
                        className="img-thumbnail"
                        style={{ width: 100, height: 100, objectFit: "cover" }}
                      />
                    )}
                    <Button
                      variant="danger"
                      size="sm"
                      className="position-absolute top-0 end-0"
                      onClick={() => removeBoL(index, "new")}
                      style={{ transform: "translate(50%, -50%)" }}
                    >
                      &times;
                    </Button>
                  </div>
                ))}
              </div>
            </Card>
          </Col>
        </Row>

        {/* Locations */}
        <Row className="mb-3">
          <Card className="p-3 mb-3">
            <h5>Pickup Location</h5>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-2">
                  <Form.Label>Location Name</Form.Label>
                  <Form.Control type="text" name="pickUpLocation.name" value={formData.pickUpLocation?.name || ""} onChange={handleChange} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-2">
                  <Form.Label>Address</Form.Label>
                  <Form.Control type="text" name="pickUpLocation.address" value={formData.pickUpLocation?.address || ""} onChange={handleChange} />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-2">
                  <Form.Label>City *</Form.Label>
                  <Form.Control
                    type="text"
                    name="pickUpLocation.city"
                    value={formData.pickUpLocation?.city || ""}
                    onChange={handleChange}
                    isInvalid={!!errors["pickUpLocation.city"]}
                  />
                  <Form.Control.Feedback type="invalid">{errors["pickUpLocation.city"]}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-2">
                  <Form.Label>State *</Form.Label>
                  <Form.Control
                    type="text"
                    name="pickUpLocation.state"
                    value={formData.pickUpLocation?.state || ""}
                    onChange={handleChange}
                    isInvalid={!!errors["pickUpLocation.state"]}
                  />
                  <Form.Control.Feedback type="invalid">{errors["pickUpLocation.state"]}</Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-2">
                  <Form.Label>ZIP Code</Form.Label>
                  <Form.Control type="text" name="pickUpLocation.zip" value={formData.pickUpLocation?.zip || ""} onChange={handleChange} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Contact Phone</Form.Label>
                  <Form.Control type="text" name="pickUpLocation.contactPhone" value={formData.pickUpLocation?.contactPhone || ""} onChange={handleChange} />
                </Form.Group>
              </Col>
            </Row>
          </Card>

          <Card className="p-3 mb-3">
            <h5>Delivery Location</h5>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-2">
                  <Form.Label>Location Name</Form.Label>
                  <Form.Control type="text" name="deliveryLocation.name" value={formData.deliveryLocation?.name || ""} onChange={handleChange} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-2">
                  <Form.Label>Address</Form.Label>
                  <Form.Control type="text" name="deliveryLocation.address" value={formData.deliveryLocation?.address || ""} onChange={handleChange} />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-2">
                  <Form.Label>City *</Form.Label>
                  <Form.Control
                    type="text"
                    name="deliveryLocation.city"
                    value={formData.deliveryLocation?.city || ""}
                    onChange={handleChange}
                    isInvalid={!!errors["deliveryLocation.city"]}
                  />
                  <Form.Control.Feedback type="invalid">{errors["deliveryLocation.city"]}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-2">
                  <Form.Label>State *</Form.Label>
                  <Form.Control
                    type="text"
                    name="deliveryLocation.state"
                    value={formData.deliveryLocation?.state || ""}
                    onChange={handleChange}
                    isInvalid={!!errors["deliveryLocation.state"]}
                  />
                  <Form.Control.Feedback type="invalid">{errors["deliveryLocation.state"]}</Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-2">
                  <Form.Label>ZIP Code</Form.Label>
                  <Form.Control type="text" name="deliveryLocation.zip" value={formData.deliveryLocation?.zip || ""} onChange={handleChange} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Contact Phone</Form.Label>
                  <Form.Control type="text" name="deliveryLocation.contactPhone" value={formData.deliveryLocation?.contactPhone || ""} onChange={handleChange} />
                </Form.Group>
              </Col>
            </Row>
          </Card>
        </Row>

        {/* Vehicle & schedule */}
        <Row className="mb-3">
          <Col md={8}>
            <Card className="p-3 mb-3">
              <h5>Vehicle Details</h5>
              <Row>
                {["make", "model", "year"].map((field) => (
                  <Col md={4} key={field} className="mb-2">
                    <Form.Group>
                      <Form.Label>{field.charAt(0).toUpperCase() + field.slice(1)}</Form.Label>
                      <Form.Control
                        type={field === "year" ? "number" : "text"}
                        name={`vehicleDetails.${field}`}
                        value={formData.vehicleDetails?.[field] || ""}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                ))}
              </Row>
            </Card>
          </Col>

          <Col md={4}>
            <Card className="p-3 h-100">
              <h5>Schedule</h5>
              <Form.Group className="mb-3">
                <Form.Label>Pickup Date *</Form.Label>
                <Form.Control
                  type="date"
                  name="pickUpDate"
                  value={formData.pickUpDate}
                  onChange={handleChange}
                  isInvalid={!!errors.pickUpDate}
                />
                <Form.Control.Feedback type="invalid">{errors.pickUpDate}</Form.Control.Feedback>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Delivery Date *</Form.Label>
                <Form.Control
                  type="date"
                  name="deliveryDate"
                  value={formData.deliveryDate}
                  onChange={handleChange}
                  isInvalid={!!errors.deliveryDate}
                />
                <Form.Control.Feedback type="invalid">{errors.deliveryDate}</Form.Control.Feedback>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Value ($)</Form.Label>
                <Form.Control type="number" name="value" value={formData.value} onChange={handleChange} />
              </Form.Group>
              <Form.Group>
                <Form.Label>Special Requirements</Form.Label>
                <Form.Control as="textarea" rows={3} name="specialRequirements" value={formData.specialRequirements} onChange={handleChange} />
              </Form.Group>
            </Card>
          </Col>
        </Row>

        {/* Vehicle Images */}
        <Row className="mb-3">
          <Col>
            <Card className="p-3">
              <h5>Vehicle Images</h5>
              <Form.Group>
                <Form.Label>Upload Images</Form.Label>
                <Form.Control type="file" multiple onChange={handleVehicleImageUpload} accept="image/*" />
                {compressingVehicle && <div className="mt-2 small text-muted">Optimizing images…</div>}
                {errors.images && <Form.Text className="text-danger">{errors.images}</Form.Text>}
              </Form.Group>

              <Form.Group className="mb-3 mt-3">
                <Form.Label>Aging (days)</Form.Label>
                <Form.Control type="number" name="aging" value={formData.aging} onChange={handleChange} min="0" />
              </Form.Group>

              <Form.Label>Assigned Date</Form.Label>
              <Form.Control type="date" name="assignedDate" value={formData.assignedDate} onChange={handleChange} />

              <Form.Label className="mt-3">Tracking</Form.Label>
              <Form.Control type="text" name="tracing" value={formData.tracing} onChange={handleChange} />

              <div className="d-flex flex-wrap mt-3">
                {existingVehicleImages.map((img, index) => (
                  <div key={`existing-${index}`} className="position-relative me-2 mb-2">
                    <img src={`${API_URL}/${img}`} alt={`Vehicle ${index}`} className="img-thumbnail" style={{ width: 100, height: 100, objectFit: "cover" }} />
                    <Button
                      variant="danger"
                      size="sm"
                      className="position-absolute top-0 end-0"
                      onClick={() => removeVehicleImage(index, "existing")}
                      style={{ transform: "translate(50%, -50%)" }}
                    >
                      &times;
                    </Button>
                  </div>
                ))}

                {newVehicleImageUrls.map((img, index) => (
                  <div key={`new-${index}`} className="position-relative me-2 mb-2">
                    <img src={img} alt={`New vehicle ${index}`} className="img-thumbnail" style={{ width: 100, height: 100, objectFit: "cover" }} />
                    <Button
                      variant="danger"
                      size="sm"
                      className="position-absolute top-0 end-0"
                      onClick={() => removeVehicleImage(index, "new")}
                      style={{ transform: "translate(50%, -50%)" }}
                    >
                      &times;
                    </Button>
                  </div>
                ))}
              </div>
            </Card>
          </Col>
        </Row>

        <div className="d-flex justify-content-end gap-2 mt-4">
          <Button variant="secondary" type="button" onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={submitting || compressingVehicle || compressingCarrier || compressingBOL}
          >
            {submitting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                {initialData ? "Updating..." : "Creating..."}
              </>
            ) : (
              initialData ? "Update Load" : "Create Load"
            )}
          </Button>
        </div>
      </Form>
    </Card>
  );
};

export default LoadForm;
